import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { SharedTenantService } from '../../super-admin-taxonomy/shared-tenant.service';

interface KnowledgeGapInsightDetail {
  id: string;
  category: string;
  language: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
  topics: {
    id: string;
    conversationId: string;
    topicTitle: string;
    topicDescription: string;
    context: string;
    confidence: number;
    createdAt: string;
  }[];
  recommendation?: {
    id: string;
    recommendationType: 'create' | 'update';
    existingArticleId?: string;
    existingArticleTitle?: string;
    ignoredAt?: string;
  };
}

interface RAGDebugResult {
  insight: {
    id: string;
    category: string;
    projectId?: string;
    topicCount: number;
  };
  searchQuery: string;
  similarArticlesFound: number;
  topScores: Array<{
    rank: number;
    score: number;
    articleId: string;
    articleTitle: string;
  }>;
}

@Component({
  selector: 'app-knowledge-gap-insight-detail',
  templateUrl: './knowledge-gap-insight-detail.component.html',
  styleUrls: ['./knowledge-gap-insight-detail.component.scss'],
})
export class KnowledgeGapInsightDetailComponent implements OnInit {
  insight: KnowledgeGapInsightDetail | null = null;
  tenantId: string | null = null;
  insightId: string | null = null;
  isLoading = false;
  error: string | null = null;
  selectedTenant: any = null;

  // RAG Debug functionality
  ragDebugResult: RAGDebugResult | null = null;
  showDebugPanel = false;
  debugLoading = false;
  debugError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private messageService: MessageService,
    private tenantsService: TenantsService,
    private sharedTenantService: SharedTenantService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(async (params) => {
      this.tenantId = params.get('tenantId');
      this.insightId = params.get('insightId');
      if (this.tenantId && this.insightId) {
        await this.loadTenantData();
        this.loadInsight();
      }
    });
  }

  async loadTenantData() {
    if (!this.tenantId) return;

    const sharedTenant = this.sharedTenantService.getSelectedTenant();
    if (sharedTenant && sharedTenant.id === this.tenantId) {
      this.selectedTenant = sharedTenant;
    } else {
      try {
        const tenant = await this.tenantsService.getTenantById(this.tenantId);
        this.selectedTenant = tenant;
        this.sharedTenantService.setSelectedTenant(tenant);
      } catch (error) {
        console.error('Failed to load tenant:', error);
      }
    }
  }

  async loadInsight() {
    if (!this.tenantId || !this.insightId) return;

    this.isLoading = true;
    this.error = null;

    try {
      const response = (await this.apiService.getPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/insights/${this.insightId}`,
      )) as KnowledgeGapInsightDetail;

      this.insight = response;
    } catch (error) {
      this.error = 'Failed to load insight: ' + (error.message || 'Unknown error');
    } finally {
      this.isLoading = false;
    }
  }

  goBack() {
    this.router.navigate(['/super-admin/knowledge-gaps', this.tenantId]);
  }

  getRecommendationUrl(recommendationId: string): string {
    if (!this.tenantId) return '#';
    return `/super-admin/knowledge-gaps/${this.tenantId}/recommendations/${recommendationId}`;
  }

  getTopicUrl(topicId: string): string {
    if (!this.tenantId) return '#';
    return `/super-admin/knowledge-gaps/${this.tenantId}/topics/${topicId}`;
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getRecommendationTypeColor(type: string): string {
    return type === 'create' ? 'success' : 'warning';
  }

  getRecommendationTypeIcon(type: string): string {
    return type === 'create' ? 'add-circle' : 'create';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getUniqueConversationCount(): number {
    if (!this.insight?.topics) return 0;
    const uniqueConversationIds = new Set(this.insight.topics.map((topic) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  getAverageConfidence(): number {
    if (!this.insight?.topics || this.insight.topics.length === 0) return 0;
    const sum = this.insight.topics.reduce((acc, topic) => acc + topic.confidence, 0);
    return sum / this.insight.topics.length;
  }

  async debugRAGSimilarity() {
    if (!this.tenantId || !this.insightId) return;

    this.debugLoading = true;
    this.debugError = null;

    try {
      const response = (await this.apiService.getPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/insights/${this.insightId}/debug-rag`,
      )) as RAGDebugResult;

      this.ragDebugResult = response;
      this.showDebugPanel = true;
      this.messageService.show('RAG similarity analysis complete', { color: 'success' });
    } catch (error) {
      this.debugError = 'Failed to analyze RAG similarity: ' + (error.message || 'Unknown error');
      this.messageService.showError(this.debugError);
    } finally {
      this.debugLoading = false;
    }
  }

  toggleDebugPanel() {
    this.showDebugPanel = !this.showDebugPanel;
    if (!this.showDebugPanel) {
      this.ragDebugResult = null;
      this.debugError = null;
    }
  }

  getArticleUrl(articleId: string): string {
    if (!articleId || !this.selectedTenant?.subdomain) return '#';

    // Generate URL to tenant's subdomain article page using the same logic as topic detail
    const currentUrl = new URL(window.location.href);

    // For dev environment, extract base domain properly
    const hostnameParts = currentUrl.hostname.split('.');
    let baseDomain: string;

    if (hostnameParts.length > 2) {
      // Skip the first part (current tenant subdomain) and use the rest
      baseDomain = hostnameParts.slice(1).join('.');
    } else {
      baseDomain = currentUrl.hostname;
    }

    // Include port if it exists
    const port = currentUrl.port ? `:${currentUrl.port}` : '';
    return `${currentUrl.protocol}//${this.selectedTenant.subdomain}.${baseDomain}${port}/dashboard/knowledge/articles/${articleId}`;
  }
}
